import { callLogsApi } from '@/services/api/callLogs';
import { CallLog, CreateCallLogRequest, UpdateCallLogRequest } from '@/types/callLog';

// Cache to prevent redundant API calls
let callLogsCache = new Map<number, { data: CallLog[], timestamp: number }>();
const CACHE_DURATION = 10000; // 10 seconds

// Global state to prevent multiple simultaneous requests
let activeRequests = new Map<string, Promise<any>>();

// Mock data for fallback when API is unavailable
const mockCallLogs: CallLog[] = [
  {
    id: 1,
    user_id: 1,
    lead_contact_id: 101,
    phone_number: "+15551234567",
    call_sid: "CAxxxxxxxxxxxxxxxxxxxxxxxxxxxxx1",
    call_status: 'completed',
    call_start_time: '2025-06-13T10:30:00Z',
    call_end_time: '2025-06-13T10:34:32Z',
    call_duration: 272,
    transcript: 'Customer was interested in our premium package. Discussed pricing and features.',
    ai_sentiment_score: 0.85,
    ai_summary: 'Positive conversation about premium package pricing',
    next_action: 'Send detailed pricing proposal',
    created_at: '2025-06-13T10:35:00Z'
  },
  {
    id: 2,
    user_id: 1,
    lead_contact_id: 102,
    phone_number: "+15551234568",
    call_sid: "CAxxxxxxxxxxxxxxxxxxxxxxxxxxxxx2",
    call_status: 'voicemail',
    call_start_time: '2025-06-13T09:15:00Z',
    call_end_time: '2025-06-13T09:15:45Z',
    call_duration: 45,
    ai_summary: 'Left voicemail message',
    next_action: 'Follow up via email',
    created_at: '2025-06-13T09:16:00Z'
  },
  {
    id: 3,
    user_id: 1,
    lead_contact_id: 103,
    phone_number: "+15551234569",
    call_sid: "CAxxxxxxxxxxxxxxxxxxxxxxxxxxxxx3",
    call_status: 'busy',
    call_start_time: '2025-06-13T08:45:00Z',
    call_duration: 0,
    ai_summary: 'Number was busy',
    next_action: 'Retry call later',
    created_at: '2025-06-13T08:45:30Z'
  }
];

class CallLogsService {
  private useMockData = false;

  async saveCallLog(callLog: CreateCallLogRequest): Promise<CallLog | null> {
    console.log('callLogsService: Attempting to save call log:', callLog);
    
    if (this.useMockData) {
      const mockLog: CallLog = {
        ...callLog,
        id: Date.now(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      console.log('callLogsService: Mock saved call log', mockLog);
      
      // Clear cache for this user to force refresh
      if (callLog.user_id) {
        callLogsCache.delete(callLog.user_id);
      }
      
      return mockLog;
    }

    try {
      const response = await callLogsApi.saveCallLog(callLog);
      if (response.success && response.data) {
        console.log('callLogsService: Successfully saved call log:', response.data);
        
        // Clear cache for this user to force refresh
        if (callLog.user_id) {
          callLogsCache.delete(callLog.user_id);
        }
        
        return response.data;
      }
      throw new Error(response.error || 'Failed to save call log');
    } catch (error) {
      console.error('callLogsService: Failed to save call log via API:', error);
      throw error;
    }
  }

  async updateCallLog(logId: number, updates: UpdateCallLogRequest): Promise<CallLog | null> {
    console.log(`callLogsService: Attempting to update call log ${logId}:`, updates);

    if (this.useMockData) {
      const logIndex = mockCallLogs.findIndex(log => log.id === logId);
      if (logIndex > -1) {
        mockCallLogs[logIndex] = { ...mockCallLogs[logIndex], ...updates, updated_at: new Date().toISOString() } as CallLog;
        console.log('callLogsService: Mock updated call log', mockCallLogs[logIndex]);
        
        // Clear all user caches since we don't know which user this belongs to
        callLogsCache.clear();
        
        return mockCallLogs[logIndex];
      }
      return null;
    }

    try {
      const response = await callLogsApi.updateCallLog(logId, updates);
      if (response.success && response.data) {
        console.log(`callLogsService: Successfully updated call log ${logId}:`, response.data);
        
        // Clear all user caches since we don't know which user this belongs to
        callLogsCache.clear();
        
        return response.data;
      }
      throw new Error(response.error || `Failed to update call log ${logId}`);
    } catch (error) {
      console.error(`callLogsService: Failed to update call log ${logId} via API:`, error);
      throw error;
    }
  }

  async getCallLogsByUserId(userId: number): Promise<CallLog[]> {
    // Check cache first
    const cached = callLogsCache.get(userId);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      console.log(`callLogsService: Using cached call logs for user ${userId}:`, cached.data.length, 'logs');
      return cached.data;
    }

    // Check if request is already in progress
    const requestKey = `user-${userId}`;
    if (activeRequests.has(requestKey)) {
      console.log(`callLogsService: Call logs request already in progress for user ${userId}, waiting...`);
      return activeRequests.get(requestKey)!;
    }

    console.log('callLogsService: Fetching call logs for user:', userId);
    
    const requestPromise = this._fetchCallLogsByUserId(userId);
    activeRequests.set(requestKey, requestPromise);

    try {
      const result = await requestPromise;
      
      // Cache the result
      callLogsCache.set(userId, {
        data: result,
        timestamp: Date.now()
      });
      
      console.log(`callLogsService: Successfully fetched and cached ${result.length} call logs for user ${userId}`);
      return result;
    } finally {
      activeRequests.delete(requestKey);
    }
  }

  private async _fetchCallLogsByUserId(userId: number): Promise<CallLog[]> {
    if (this.useMockData) {
      const userLogs = mockCallLogs.filter(log => log.user_id === userId);
      console.log(`callLogsService: Mock data - returning ${userLogs.length} logs for user ${userId}`);
      return userLogs;
    }

    try {
      const response = await callLogsApi.getCallLogsByUserId(userId);
      if (response.success && response.data) {
        console.log(`callLogsService: API success - received ${response.data.length} logs for user ${userId}`);
        return response.data;
      }
      throw new Error(response.error || 'Failed to fetch call logs');
    } catch (error) {
      console.warn('callLogsService: API failed, falling back to mock data for call logs fetch:', error);
      this.useMockData = true;
      const userLogs = mockCallLogs.filter(log => log.user_id === userId);
      console.log(`callLogsService: Fallback - returning ${userLogs.length} mock logs for user ${userId}`);
      return userLogs;
    }
  }

  async getAllCallLogs(): Promise<CallLog[]> {
    if (this.useMockData) {
      return mockCallLogs;
    }

    try {
      const response = await callLogsApi.getAllCallLogs();
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.error || 'Failed to fetch call logs');
    } catch (error) {
      console.warn('callLogsService: API failed, falling back to mock data');
      this.useMockData = true;
      return mockCallLogs;
    }
  }

  formatCallLogForHistory(log: CallLog) {
    console.log('callLogsService: Formatting call log for history:', log);
    
    const phoneNumber = log.phone_number || this.extractPhoneFromLog(log) || '+1 (555) 000-0000';
    
    const formatted = {
      id: log.id?.toString() || Date.now().toString(),
      number: phoneNumber,
      name: this.extractContactNameFromLog(log),
      timestamp: new Date(log.call_start_time),
      duration: this.formatDuration(log.call_duration),
      outcome: this.mapCallStatusToOutcome(log.call_status),
      direction: 'outbound' as 'outbound' | 'inbound',
      quality: log.ai_sentiment_score ? this.mapSentimentToQuality(log.ai_sentiment_score) : undefined,
      notes: log.ai_summary,
      call_price: log.call_price,
      call_price_unit: log.call_price_unit
    };
    
    console.log('callLogsService: Formatted history item:', formatted);
    return formatted;
  }

  private extractPhoneFromLog(log: CallLog): string | undefined {
    const text = log.ai_summary || log.transcript || '';
    const phoneMatch = text.match(/\+?1?[-.\s]?\(?(\d{3})\)?[-.\s]?(\d{3})[-.\s]?(\d{4})/);
    if (phoneMatch) {
      return `+1 (${phoneMatch[1]}) ${phoneMatch[2]}-${phoneMatch[3]}`;
    }
    return undefined;
  }

  private extractContactNameFromLog(log: CallLog): string | undefined {
    return undefined;
  }

  private formatDuration(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  private mapCallStatusToOutcome(status: string): string {
    switch (status) {
      case 'completed':
      case 'open':
        return 'connected';
      case 'busy':
        return 'busy';
      case 'voicemail':
        return 'voicemail';
      case 'failed':
        return 'failed';
      case 'no-answer':
        return 'no-answer';
      default:
        return 'no-answer';
    }
  }

  private mapSentimentToQuality(score: number): 'excellent' | 'good' | 'poor' {
    if (score >= 0.7) return 'excellent';
    if (score >= 0.4) return 'good';
    return 'poor';
  }
}

export const callLogsService = new CallLogsService();
